import React, { useState, useEffect } from 'react';
import {
  Camera,
  Upload,
  Search,
  Filter,
  MapPin,
  CheckCircle2,
  Calendar,
  Building2,
  Ticket,
  Maximize2,
  Sparkles,
  Layers,
  ArrowRight,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import { WorkEvidenceItem, TicketItem } from '../types/index.ts';

interface WorkEvidencePageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const WorkEvidencePage: React.FC<WorkEvidencePageProps> = ({ onNavigate }) => {
  const [evidenceList, setEvidenceList] = useState<WorkEvidenceItem[]>([]);
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPhase, setFilterPhase] = useState<'ALL' | 'BEFORE' | 'DURING' | 'AFTER' | 'SURVEY' | 'COMPLETION'>('ALL');
  const [selectedTicketId, setSelectedTicketId] = useState<string>('ALL');
  const [search, setSearch] = useState('');

  // Before & After Compare Modal
  const [showComparison, setShowComparison] = useState(false);
  const [compareTicketId, setCompareTicketId] = useState<number>(1);

  // Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    ticketId: '1',
    phase: 'BEFORE' as 'BEFORE' | 'DURING' | 'AFTER' | 'SURVEY' | 'COMPLETION',
    caption: '',
    photoUrl: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=800&auto=format&fit=crop&q=80',
    latitude: '24.8214',
    longitude: '67.0321',
    isGpsVerified: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [evRes, tktRes] = await Promise.all([
        fetch('/api/evidence').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(evRes)) setEvidenceList(evRes);
      if (Array.isArray(tktRes)) {
        setTickets(tktRes);
        if (tktRes.length > 0 && !uploadForm.ticketId) {
          setUploadForm((prev) => ({ ...prev, ticketId: String(tktRes[0].id) }));
          setCompareTicketId(tktRes[0].id);
        }
      }
    } catch (err: any) {
      console.warn('Work evidence load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const res = await fetch('/api/evidence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: Number(uploadForm.ticketId),
          phase: uploadForm.phase,
          caption: uploadForm.caption,
          photoUrl: uploadForm.photoUrl,
          latitude: uploadForm.latitude,
          longitude: uploadForm.longitude,
          isGpsVerified: uploadForm.isGpsVerified,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Evidence photo saved successfully (${data.phase})`);
        setShowUploadModal(false);
        setUploadForm({
          ticketId: tickets.length > 0 ? String(tickets[0].id) : '1',
          phase: 'BEFORE',
          caption: '',
          photoUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&auto=format&fit=crop&q=80',
          latitude: '24.8214',
          longitude: '67.0321',
          isGpsVerified: true,
        });
        await fetchData();
      } else {
        setToastMessage(data.error || 'Failed to save evidence');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Upload error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const filteredEvidence = evidenceList.filter((item) => {
    const matchesPhase = filterPhase === 'ALL' || item.phase === filterPhase;
    const matchesTicket = selectedTicketId === 'ALL' || String(item.ticketId) === selectedTicketId;
    const matchesSearch = item.caption.toLowerCase().includes(search.toLowerCase());
    return matchesPhase && matchesTicket && matchesSearch;
  });

  // Compare images
  const beforePhotos = evidenceList.filter((e) => e.ticketId === compareTicketId && e.phase === 'BEFORE');
  const afterPhotos = evidenceList.filter((e) => e.ticketId === compareTicketId && (e.phase === 'AFTER' || e.phase === 'COMPLETION' || e.phase === 'DURING'));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Work Evidence &amp; Photo Documentation
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              Photographic Proof
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Site survey, pre-repair baseline, execution progress, and handover photos geotagged with GPS satellite verification.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setShowComparison(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Layers className="w-3.5 h-3.5 text-blue-600" />
            Before vs After Compare
          </button>

          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors"
          >
            <Upload className="w-3.5 h-3.5 text-blue-200" />
            Upload Evidence
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Phase Segmented Control */}
          <div className="flex items-center gap-1 overflow-x-auto text-[11px]">
            {(['ALL', 'SURVEY', 'BEFORE', 'DURING', 'AFTER', 'COMPLETION'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setFilterPhase(p)}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                  filterPhase === p
                    ? 'bg-blue-700 text-white shadow-xs'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedTicketId}
              onChange={(e) => setSelectedTicketId(e.target.value)}
              className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="ALL">All Branch Tickets</option>
              {tickets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.ticketNumber} — {t.branchName}
                </option>
              ))}
            </select>

            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search caption..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-44"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Photo Grid Gallery */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredEvidence.length === 0 ? (
          <div className="col-span-full p-12 text-center text-slate-400 bg-white border border-slate-200 rounded-xl">
            <Camera className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-xs">No evidence photos found matching this filter.</p>
          </div>
        ) : (
          filteredEvidence.map((photo) => {
            const ticket = tickets.find((t) => t.id === photo.ticketId);
            return (
              <div
                key={photo.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs hover:shadow-md transition-shadow flex flex-col text-xs"
              >
                <div className="relative aspect-video bg-slate-900 overflow-hidden group">
                  <img
                    src={photo.photoUrl}
                    alt={photo.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                  <div className="absolute top-2 left-2">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase ${
                        photo.phase === 'BEFORE'
                          ? 'bg-amber-500 text-white'
                          : photo.phase === 'AFTER'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-blue-600 text-white'
                      }`}
                    >
                      {photo.phase}
                    </span>
                  </div>

                  {photo.isGpsVerified && (
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/90 text-white flex items-center gap-1 backdrop-blur-xs">
                        <ShieldCheck className="w-3 h-3" />
                        GPS
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-3.5 flex flex-col flex-1 space-y-2">
                  <div className="font-medium text-slate-800 line-clamp-2 leading-relaxed">
                    {photo.caption}
                  </div>

                  <div className="mt-auto pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                    <span className="font-mono font-bold text-slate-800">
                      {ticket ? ticket.ticketNumber : `TKT #${photo.ticketId}`}
                    </span>
                    <span>{new Date(photo.uploadedAt).toLocaleDateString()}</span>
                  </div>

                  {photo.latitude && photo.longitude && (
                    <div className="text-[10px] text-slate-400 font-mono truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      {photo.latitude}, {photo.longitude}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Before vs After Side-by-Side Modal */}
      {showComparison && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Before &amp; After Photo Comparator</h3>
              </div>
              <button
                onClick={() => setShowComparison(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-5 space-y-5 text-xs">
              <div className="flex items-center gap-3">
                <label className="text-slate-700 font-semibold">Select Ticket:</label>
                <select
                  value={compareTicketId}
                  onChange={(e) => setCompareTicketId(Number(e.target.value))}
                  className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {tickets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.ticketNumber} — {t.branchName} ({t.subject})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Before Column */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-amber-800 text-xs uppercase tracking-wider">
                      Initial Condition (Before Work)
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                      BEFORE
                    </span>
                  </div>

                  {beforePhotos.length > 0 ? (
                    beforePhotos.map((p) => (
                      <div key={p.id} className="space-y-1.5">
                        <img
                          src={p.photoUrl}
                          alt="Before"
                          className="w-full h-56 object-cover rounded-lg border border-slate-200 shadow-xs"
                        />
                        <p className="text-[11px] text-slate-700 font-medium">{p.caption}</p>
                        <div className="text-[10px] text-slate-400 font-mono">
                          GPS: {p.latitude}, {p.longitude}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-56 flex items-center justify-center text-slate-400 text-xs border border-dashed border-slate-200 rounded-lg">
                      No BEFORE photos logged for this ticket yet.
                    </div>
                  )}
                </div>

                {/* After Column */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-emerald-800 text-xs uppercase tracking-wider">
                      Rectified Finish (After Work)
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      AFTER
                    </span>
                  </div>

                  {afterPhotos.length > 0 ? (
                    afterPhotos.map((p) => (
                      <div key={p.id} className="space-y-1.5">
                        <img
                          src={p.photoUrl}
                          alt="After"
                          className="w-full h-56 object-cover rounded-lg border border-slate-200 shadow-xs"
                        />
                        <p className="text-[11px] text-slate-700 font-medium">{p.caption}</p>
                        <div className="text-[10px] text-slate-400 font-mono">
                          GPS: {p.latitude}, {p.longitude}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-56 flex items-center justify-center text-slate-400 text-xs border border-dashed border-slate-200 rounded-lg">
                      No AFTER photos logged for this ticket yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setShowComparison(false)}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800"
                >
                  Close Comparator
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Photo Evidence Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Upload Site Evidence Photo</h3>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Target Ticket</label>
                <select
                  required
                  value={uploadForm.ticketId}
                  onChange={(e) => setUploadForm({ ...uploadForm, ticketId: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  {tickets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.ticketNumber} — {t.branchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Execution Phase</label>
                <select
                  value={uploadForm.phase}
                  onChange={(e) => setUploadForm({ ...uploadForm, phase: e.target.value as any })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="SURVEY">SURVEY (Initial Site Inspection)</option>
                  <option value="BEFORE">BEFORE (Pre-repair damage condition)</option>
                  <option value="DURING">DURING (Work in execution / chemicals)</option>
                  <option value="AFTER">AFTER (Completed repair &amp; paint finish)</option>
                  <option value="COMPLETION">COMPLETION (Joint branch sign-off)</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Photo Description / Caption</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sika chemical application over cracked seepage wall"
                  value={uploadForm.caption}
                  onChange={(e) => setUploadForm({ ...uploadForm, caption: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Image URL / File Path</label>
                <input
                  type="url"
                  required
                  value={uploadForm.photoUrl}
                  onChange={(e) => setUploadForm({ ...uploadForm, photoUrl: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">GPS Latitude</label>
                  <input
                    type="text"
                    value={uploadForm.latitude}
                    onChange={(e) => setUploadForm({ ...uploadForm, latitude: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">GPS Longitude</label>
                  <input
                    type="text"
                    value={uploadForm.longitude}
                    onChange={(e) => setUploadForm({ ...uploadForm, longitude: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                <input
                  type="checkbox"
                  checked={uploadForm.isGpsVerified}
                  onChange={(e) => setUploadForm({ ...uploadForm, isGpsVerified: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[11px] font-medium">Verify image was captured within branch perimeter</span>
              </label>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Uploading...' : 'Save Evidence'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
